import axios from 'axios';
import { LMS_BASEURL } from '../../../../Api/api-constant';

const QUESTION_BANK_URL = `${LMS_BASEURL}/api/questionBank`;

export type BankOption = {
  optionNumber: number;
  optionType: string;
  value: string;
  optionFile?: string;
};

export type BankQuestionLocation = {
  id: string;
  courseId: number | string;
  courseName: string;
  levelId: number | string;
  courseLevel: string;
  lessonNumber: string | number;
};

export type BankQuestion = {
  id: string;
  question: string;
  questionType: string;
  questionFile: string;
  options: BankOption[];
  answer: string[];
  answerType: 'single' | 'multiple';
  answerExplanation: string;
  courseName: string;
  courseLevel: string;
  courseId: number;
  levelId: number;
  lessonNumber: string | number;
  selectedOption: number;
  questionGroupId?: string;
  locations?: BankQuestionLocation[];
};

export type QuestionBankFilters = {
  courseId: number;
  levelId: number;
  lessonNumber: string;
};

export type SaveQuestionPayload = Omit<BankQuestion, 'id' | 'answer' | 'questionType' | 'selectedOption' | 'locations'> & {
  correctOptionNumbers: number[];
};

export type MediaUploadRequest = {
  fileName: string;
  courseName: string;
  courseLevel: string;
  lessonNumber: string | number;
  mediaRole: 'question' | `option-${number}`;
};

export type MediaUploadResponse = {
  uploadUrl: string;
  cloudFrontUrl: string;
  objectKey: string;
  contentType: string;
};

export const getBankQuestions = async (filters: QuestionBankFilters): Promise<BankQuestion[]> => {
  const response = await axios.get(QUESTION_BANK_URL, { params: filters });
  return response.data?.questions ?? [];
};

export const createBankQuestion = async (payload: SaveQuestionPayload): Promise<BankQuestion> => {
  const response = await axios.post(QUESTION_BANK_URL, payload);
  return response.data?.question;
};

export const updateBankQuestion = async (id: string, payload: SaveQuestionPayload): Promise<BankQuestion> => {
  const response = await axios.put(`${QUESTION_BANK_URL}/${encodeURIComponent(id)}`, payload);
  return response.data?.question;
};

export const deleteBankQuestion = async (id: string): Promise<void> => {
  await axios.delete(`${QUESTION_BANK_URL}/${encodeURIComponent(id)}`);
};

export const getBankMediaUploadUrl = async (payload: MediaUploadRequest): Promise<MediaUploadResponse> => {
  const response = await axios.post(`${QUESTION_BANK_URL}/upload-url`, payload);
  return response.data;
};

export const uploadBankMedia = async (uploadUrl: string, file: File, contentType: string): Promise<void> => {
  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': contentType },
    skipAuth: true,
  } as any);
};
